import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Animated, Dimensions, Modal, Platform } from 'react-native';
import styled from 'styled-components/native';

import { Feather } from '@expo/vector-icons';

import elementIDs from '../../../elementIDs';

import Colors from '../../constants/Colors';
import ListItem from '../list/ListItem';
import ListItemIcon from '../list/ListItemIcon';
import ListItemText from '../list/ListItemText';
import * as ImageManipulator from 'expo-image-manipulator';
import * as ImagePicker from 'expo-image-picker';
import { FetchError } from '../../api/api';
import { UploadFile, useUploadFiles } from '../../hooks/upload/useUploadFiles';
import ActivityIndicatorScreen from '../ActivityIndicatorScreen';

const StyledBackdrop = styled.View`
  height: 100%;
  width: 100%;
  background: #00000066;
`;

const StyledBackdropTransparentTouchable = styled.TouchableOpacity`
  flex: 1 1 auto;
  width: 100%;
`;

const StyledActionSheetView = styled(Animated.View)`
  background: #fff;
`;

interface ImageActionSheetProps {
  visible: boolean;
  /**
   * Specify uploadPath to auto upload to S3 when image is selected.
   */
  uploadPath?: string;
  /**
   * Specify array of image resize constraints (pixels). onImage will return an
   * array of resized images.
   */
  resize?: number[];
  presignBody?: any | any[];
  selectText?: string;
  captureText?: string;
  pickerOptions?: ImagePicker.ImagePickerOptions;
  disableCameraCapture?: boolean;
  onSelectImage?: (image: ImagePicker.ImagePickerResult) => void;
  onResizeImages?: (images: ImageManipulator.ImageResult[]) => void;
  onResizeImageFiles?: (files: File[]) => void;
  onUploadedImages?: (uploadedFiles: (UploadFile | undefined)[]) => void;
  onUploadVideos?: (uploadedFiles: (UploadFile | undefined)[]) => void;
  onUploading?: (uploading: boolean) => void;
  onUploadError?: (error: FetchError) => void;
  onClose: () => void;
}

const ImageActionSheet: React.FC<ImageActionSheetProps> = ({
  visible,
  uploadPath = '',
  resize,
  presignBody = { mimeType: 'image/jpeg' },
  selectText = 'Take a new image/video',
  captureText = 'Choose an existing image/video',
  pickerOptions = {
    mediaTypes: ['images', 'videos'],
    allowsEditing: true,
    aspect: [1, 1],
    quality: 1
  },
  disableCameraCapture,
  onSelectImage,
  onResizeImages,
  onResizeImageFiles,
  onUploadedImages,
  onUploadVideos,
  onUploading,
  onUploadError,
  onClose
}) => {
  if ((onResizeImages || onResizeImageFiles) && !resize) {
    throw new Error('No resize props provided.');
  }

  const onUploadingRef = useRef(onUploading);
  onUploadingRef.current = onUploading;
  const onUploadErrorRef = useRef(onUploadError);
  onUploadErrorRef.current = onUploadError;
  const [resizing, setResizing] = useState<boolean>();

  // handle uploading if S3 uploadPath is specified
  const { upload, error, uploading } = useUploadFiles(uploadPath);

  useEffect(() => {
    onUploadingRef.current?.(uploading);
  }, [uploading]);

  useEffect(() => {
    if (error) onUploadErrorRef.current?.(error);
  }, [error]);

  const getFileTypeFromMime = (mimeType: string) => {
    switch (mimeType) {
      case 'image/jpeg':
        return { fileType: 'jpg', mimeType, type: 'image' as const };
      case 'image/png':
        return { fileType: 'png', mimeType, type: 'image' as const };
      case 'image/heic':
        return { fileType: 'heic', mimeType, type: 'image' as const };
      case 'image/heif':
        return { fileType: 'heif', mimeType, type: 'image' as const };
      case 'image/gif':
        return { fileType: 'gif', mimeType, type: 'image' as const };
      case 'video/mp4':
        return { fileType: 'mp4', mimeType, type: 'video' as const };
      case 'video/x-m4v':
        return { fileType: 'm4v', mimeType, type: 'video' as const };
      case 'video/quicktime':
        return {
          fileType: 'mp4',
          mimeType: 'video/mp4',
          type: 'video' as const
        };
    }
    return undefined;
  };

  const getFileType = (uri: string, mimeType?: string | null) => {
    let fileType = '';
    if (Platform.OS !== 'web') {
      const arr = uri.split('.');
      if (arr) {
        fileType = arr[arr.length - 1].toLowerCase();
      }
    } else if (uri.startsWith('data:')) {
      const webMimeType = uri.split(':')[1].split(';')[0];
      return getFileTypeFromMime(webMimeType);
    } else if (mimeType) {
      return getFileTypeFromMime(mimeType);
    }
    if (fileType) {
      switch (fileType) {
        case 'jpeg':
        case 'jpg':
          return { fileType, mimeType: 'image/jpeg', type: 'image' };
        case 'png':
          return { fileType, mimeType: 'image/png', type: 'image' };
        case 'heic':
          return { fileType, mimeType: 'image/heic', type: 'image' };
        case 'heif':
          return { fileType, mimeType: 'image/heif', type: 'image' };
        case 'gif':
          return { fileType, mimeType: 'image/gif', type: 'image' };
        case 'mp4':
          return { fileType, mimeType: 'video/mp4', type: 'video' };
        case 'm4v':
          return { fileType, mimeType: 'video/x-m4v', type: 'video' };
        case 'mov':
          return { fileType: 'mp4', mimeType: 'video/mp4', type: 'video' };
      }
    }
    return mimeType ? getFileTypeFromMime(mimeType) : undefined;
  };

  const getAssetMediaType = (asset: ImagePicker.ImagePickerAsset) =>
    getFileType(asset.uri, asset.mimeType)?.type ?? asset.type;

  const handleImagePicker = useCallback(
    async (result: ImagePicker.ImagePickerResult) => {
      if (result.canceled) return;
      setResizing(true);
      try {
        onSelectImage?.(result);
        const images: ProcessedImageResult[] = [];
        const videos: {
          uri: string;
          base64?: string;
          mimeType?: string;
          file?: File;
        }[] = [];
        // resize images if resize array is provided
        if (resize) {
          for (const size of resize) {
            for (const asset of result.assets) {
              const type = getAssetMediaType(asset);
              if (type === 'image') {
                images.push(await resizeImage(asset, size));
              } else if (type === 'video') {
                videos.push({
                  uri: asset.uri,
                  base64: asset.base64 || undefined,
                  mimeType: asset.mimeType,
                  file: asset.file
                });
              }
            }
          }
          onResizeImages?.(images);
        } else {
          for (const asset of result.assets) {
            if (getAssetMediaType(asset) === 'image') {
              if (Platform.OS === 'web' && asset.file) {
                images.push({
                  uri: '',
                  width: asset.width,
                  height: asset.height,
                  sourceFile: asset.file
                });
              } else {
                images.push({
                  uri: asset.uri,
                  width: asset.width,
                  height: asset.height,
                  base64: asset.base64 || undefined
                });
              }
            } else if (getAssetMediaType(asset) === 'video') {
              videos.push({
                uri: asset.uri,
                base64: asset.base64 || undefined,
                mimeType: asset.mimeType,
                file: asset.file
              });
            }
          }
        }

        // skip if not returning resized image files and not uploading
        if (!onResizeImageFiles && !uploadPath) {
          return;
        }

        // create resized image files
        const imageFiles = await Promise.all(
          images.map(async (image, index) => {
            if (image.sourceFile) {
              return ensureFileMimeType(image.sourceFile);
            }
            const type = getFileType(image.uri);
            return getFileFromUri(
              image.uri,
              `image${resize?.[index] ? `-${resize[index]}` : ''}.${
                type?.fileType ? type.fileType : 'jpg'
              }`,
              type?.mimeType || 'image/jpeg'
            );
          })
        );
        const videoFiles = await Promise.all(
          videos.map(video => {
            if (Platform.OS === 'web' && video.file) {
              return video.file;
            }
            const type = getFileType(video.uri, video.mimeType);
            return getFileFromUri(
              video.uri,
              `video.${type?.fileType ? type.fileType : 'mp4'}`,
              type?.mimeType || 'video/mp4'
            );
          })
        );

        onResizeImageFiles?.(imageFiles);

        // upload files if uploadPath is provided
        if (uploadPath) {
          if (imageFiles) {
            const uploadedImages = await Promise.all(
              imageFiles.map((imageFile, index) => {
                const body = Array.isArray(presignBody)
                  ? presignBody[index]
                  : presignBody;
                return upload(imageFile, {
                  presignBody: {
                    ...body,
                    mimeType: imageFile.type || body?.mimeType || 'image/jpeg'
                  }
                });
              })
            );
            onUploadedImages?.(uploadedImages);
          }
          if (videoFiles) {
            const uploadedVideos = await Promise.all(
              videoFiles.map(videoFile => upload(videoFile))
            );
            onUploadVideos?.(uploadedVideos);
          }
        }
      } catch (err) {
        onUploadErrorRef.current?.({
          statusCode: 0,
          error: 'ImageUploadError',
          message:
            err instanceof Error
              ? err.message
              : 'Failed to process or upload image'
        });
      } finally {
        setResizing(false);
      }
    },
    [
      onResizeImageFiles,
      onResizeImages,
      onSelectImage,
      onUploadVideos,
      onUploadedImages,
      presignBody,
      resize,
      upload,
      uploadPath
    ]
  );

  // Animate slide up from bottom
  const { height } = Dimensions.get('window');
  const animated = useRef(new Animated.Value(height)).current;
  useEffect(() => {
    Animated.timing(animated, {
      toValue: visible ? 0 : height,
      duration: 300,
      useNativeDriver: Platform.OS === 'ios' || Platform.OS === 'android'
    }).start();
  }, [animated, visible, height]);

  const handleCameraAction = useCallback(
    async (
      mediaTypes: ImagePicker.MediaType | ImagePicker.MediaType[] = [
        'images',
        'videos'
      ]
    ) => {
      if (Platform.OS !== 'web') {
        // check if permission granted
        const { status } = await ImagePicker.getCameraPermissionsAsync();
        if (status !== 'granted') {
          // if no, ask for permission (first time only)
          const { status } = await ImagePicker.requestCameraPermissionsAsync();
          if (status !== 'granted') {
            // if permission denied, can't access camera
            alert('Sorry, we need camera permissions to make this work!');
            return;
          }
        }
      }
      const result = await ImagePicker.launchCameraAsync({
        ...pickerOptions,
        mediaTypes
      });
      await handleImagePicker(result);
    },
    [handleImagePicker, pickerOptions]
  );

  return (
    <Modal
      animationType='fade'
      hardwareAccelerated
      visible={visible}
      transparent
    >
      <StyledBackdrop>
        <StyledBackdropTransparentTouchable onPress={() => onClose()} />
        {!uploading && !resizing ? (
          <StyledActionSheetView
            style={{ transform: [{ translateY: animated }] }}
          >
            {!disableCameraCapture &&
              (Platform.OS === 'android' ? (
                // Provide separate buttons for either image or video capture as Android's provided camera capture intents doesn't support both image and video at the same time.
                <>
                  <ListItem
                    onPress={() => handleCameraAction(['images'])}
                    testID={elementIDs.takeImageID}
                  >
                    <ListItemIcon>
                      <Feather
                        name='camera'
                        size={29}
                        color={Colors.primaryPurple}
                      />
                    </ListItemIcon>
                    <ListItemText primary={'Take a new image'} />
                  </ListItem>
                  <ListItem onPress={() => handleCameraAction(['videos'])}>
                    <ListItemIcon>
                      <Feather
                        name='video'
                        size={29}
                        color={Colors.primaryPurple}
                      />
                    </ListItemIcon>
                    <ListItemText primary={'Take a new video'} />
                  </ListItem>
                </>
              ) : (
                <ListItem onPress={() => handleCameraAction()}>
                  <ListItemIcon>
                    <Feather
                      name='camera'
                      size={29}
                      color={Colors.primaryPurple}
                    />
                  </ListItemIcon>
                  <ListItemText primary={selectText} />
                </ListItem>
              ))}
            <ListItem
              onPress={async () => {
                if (Platform.OS !== 'web') {
                  // check if permission granted
                  const { status } =
                    await ImagePicker.getMediaLibraryPermissionsAsync();
                  if (status !== 'granted') {
                    // if no, ask for permission (first time only)
                    const { status } =
                      await ImagePicker.requestMediaLibraryPermissionsAsync();
                    if (status !== 'granted') {
                      // if permission denied, can't access media
                      alert(
                        'Sorry, we need media library permissions to make this work!'
                      );
                      return;
                    }
                  }
                }
                const result =
                  await ImagePicker.launchImageLibraryAsync(pickerOptions);
                await handleImagePicker(result);
              }}
            >
              <ListItemIcon>
                <Feather name='image' size={29} color={Colors.primaryPurple} />
              </ListItemIcon>
              <ListItemText primary={captureText} />
            </ListItem>
            <ListItem onPress={() => onClose()}>
              <ListItemIcon>
                <Feather name='x' size={29} color={Colors.primaryPurple} />
              </ListItemIcon>
              <ListItemText primary='Cancel' />
            </ListItem>
          </StyledActionSheetView>
        ) : (
          <ActivityIndicatorScreen modal={true} color={Colors.primary} />
        )}
      </StyledBackdrop>
    </Modal>
  );
};

export default ImageActionSheet;

export const getFileFromUri = async (
  uri: string,
  filename: string,
  mimeType: string
) => {
  const response = await fetch(uri);
  const blob = await response.blob();
  return new File([blob], filename, { type: mimeType });
};

type ProcessedImageResult = ImageManipulator.ImageResult & {
  sourceFile?: File;
};

const getHighEfficiencyImageMeta = (
  asset: Pick<
    ImagePicker.ImagePickerAsset,
    'mimeType' | 'fileName' | 'file' | 'uri'
  >
) => {
  const mimeType = asset.mimeType?.toLowerCase();
  if (mimeType === 'image/heif') {
    return { fileType: 'heif', mimeType: 'image/heif' };
  }
  if (mimeType === 'image/heic') {
    return { fileType: 'heic', mimeType: 'image/heic' };
  }
  const fileName =
    asset.fileName?.toLowerCase() ??
    asset.file?.name.toLowerCase() ??
    asset.uri.toLowerCase();
  if (fileName.includes('.heif')) {
    return { fileType: 'heif', mimeType: 'image/heif' };
  }
  if (fileName.includes('.heic')) {
    return { fileType: 'heic', mimeType: 'image/heic' };
  }
  return undefined;
};

const isHeicAsset = (asset: ImagePicker.ImagePickerAsset) =>
  getHighEfficiencyImageMeta(asset) !== undefined;

const ensureFileMimeType = (file: File) => {
  if (file.type) {
    return file;
  }
  const extension = file.name.split('.').pop()?.toLowerCase();
  if (extension === 'heic') {
    return new File([file], file.name, { type: 'image/heic' });
  }
  if (extension === 'heif') {
    return new File([file], file.name, { type: 'image/heif' });
  }
  return file;
};

const getWebImageFileFromAsset = async (
  asset: ImagePicker.ImagePickerAsset
) => {
  if (asset.file) {
    return ensureFileMimeType(asset.file);
  }
  const highEfficiencyImage = getHighEfficiencyImageMeta(asset);
  const mimeType =
    highEfficiencyImage?.mimeType ?? asset.mimeType ?? 'image/jpeg';
  const fileName =
    asset.fileName ?? `image.${highEfficiencyImage?.fileType ?? 'jpg'}`;
  return getFileFromUri(asset.uri, fileName, mimeType);
};

const loadHtmlImage = (src: string) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () =>
      reject(
        new Error(
          'Unable to load this image. HEIC files may not be supported in this browser.'
        )
      );
    img.src = src;
  });

const resizeImageOnWeb = async (
  asset: ImagePicker.ImagePickerAsset,
  size: number
): Promise<ImageManipulator.ImageResult> => {
  const blob = asset.file ?? (await (await fetch(asset.uri)).blob());
  const objectUrl = URL.createObjectURL(blob);
  try {
    const img = await loadHtmlImage(objectUrl);
    const width = asset.width ?? img.width;
    const height = asset.height ?? img.height;
    const dimension = width > height ? 'width' : 'height';
    const sourceSize = dimension === 'width' ? width : height;
    const scale = Math.min(1, size / sourceSize);
    const targetWidth = Math.round(width * scale);
    const targetHeight = Math.round(height * scale);
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;
    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Failed to prepare image for upload');
    }
    context.drawImage(img, 0, 0, targetWidth, targetHeight);
    const jpegBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        result =>
          result
            ? resolve(result)
            : reject(new Error('Failed to convert image to JPEG')),
        'image/jpeg',
        0.7
      );
    });
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () =>
        reject(new Error('Failed to read converted image'));
      reader.readAsDataURL(jpegBlob);
    });
    return {
      uri: dataUrl,
      width: targetWidth,
      height: targetHeight,
      base64: dataUrl.split(',')[1]
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

const resizeImage = async (
  image: ImagePicker.ImagePickerAsset,
  size: number
): Promise<ProcessedImageResult> => {
  if (Platform.OS === 'web') {
    try {
      return await resizeImageOnWeb(image, size);
    } catch (error) {
      if (!isHeicAsset(image)) {
        throw error;
      }
      return {
        uri: '',
        width: image.width ?? 0,
        height: image.height ?? 0,
        sourceFile: await getWebImageFileFromAsset(image)
      };
    }
  }
  const dimension = image.width > image.height ? 'width' : 'height';
  return ImageManipulator.manipulateAsync(
    image.uri,
    [{ resize: { [dimension]: size } }],
    { compress: 0.7, format: ImageManipulator.SaveFormat.JPEG, base64: true }
  );
};
